import { useQueryClient } from '@tanstack/react-query';
import { useDependency } from 'ditox-react';
import { noop } from 'lodash-es';
import { useCallback } from 'react';
import type { ConversationFormDataType } from '@/components/conversations/conversation-form';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import useSendMessage, {
  alphaNumericalNanoId,
  generateClientMessageId,
  generateMessagePackage,
} from '@/hooks/conversations/use-send-message';
import useUploadMessageFile from '@/hooks/files/use-upload-message-file';
import { generateSessionSettingsQueryOptions } from '@/hooks/sessions/use-session-settings';
import useUserInfo from '@/hooks/users/use-user-info';
import {
  ConversationMessageContentType,
  type ConversationMessageTypeUnion,
  transformConversationMessageToSendMessage,
  transformSendMessageToConversationMessage,
} from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import type { SendChatMessageType, SendGroupChatMessageType } from '@/models/messages/send-message';
import { SENTRY_LOG, simpleLogger } from '@/services/sentry';
import { useMessageStore } from '@/stores/message';
import { addToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { FileType } from '@/utils/file-type';
import { convertUploadableToBlob, convertUploadableToDataURL, convertUrlToUploadable } from '@/utils/uploadable';

// ---------- Small helpers to improve readability ----------
const isInlineUrl = (url: string | null | undefined) => !!url && (url.startsWith('data:') || url.startsWith('blob:'));
const isMessageAttachmentUploadable = (message: Pick<ConversationMessageTypeUnion, 'MsgType' | 'Message'>) => {
  if (!isInlineUrl(message.Message)) return false;

  switch (message.MsgType) {
    case ConversationMessageContentType.Image:
    case ConversationMessageContentType.Video:
    case ConversationMessageContentType.Audio:
    case ConversationMessageContentType.File:
      return true;
    default:
      return false;
  }
};
const getAttachmentCategory = (mime?: string) => mime?.split('/').at(0)?.toLowerCase();

export default function useMessageWebSocketDispatch(type: ConversationType, id: number | null | undefined) {
  const webSocketManager = useDependency(WEB_SOCKET_MANAGER_TOKEN);
  const queryClient = useQueryClient();
  const { data: userInfo } = useUserInfo();
  const { mutateAsync: sendMessage, isPending: isSendingMessage } = useSendMessage();
  const { upload, isLoading: isUploadingFile } = useUploadMessageFile(type, id);

  // Retry sending a stale message if it's eligible
  const retry = (message: ConversationMessageTypeUnion) => {
    const isStale = Date.now() - message.TimeStamp >= 5000;
    if (message.Status !== null || !isStale) return;

    const newMessage = useMessageStore.getState().unmarkStale(message);
    const sendable = transformConversationMessageToSendMessage(newMessage);

    return handleSendMessage(sendable);
  };

  // Upload inline blob/data attachments and normalize the message before sending
  const uploadAttachmentIfNeeded = async <T extends SendChatMessageType | SendGroupChatMessageType>(
    message: T,
  ): Promise<T> => {
    const [content, ...rest] = message.Message.split('|');

    if (message.MsgType === ConversationMessageContentType.Text) return message; // nothing to do

    if (!content) return message;

    if (!isInlineUrl(content) || rest.length < 3) return message; // not an inline file message

    try {
      const [attachmentType, name, size, compress, ...extra] = rest;
      const attachment = convertUrlToUploadable(content, attachmentType);
      if (name) attachment.name = name;
      if (size) attachment.size = Number.parseInt(size);
      if (compress === '1') attachment.compress = true;

      switch (getAttachmentCategory(attachmentType)) {
        case 'audio': {
          // ensure audio is compressed before upload
          attachment.compress = true;
          const { FileUrl } = await upload(attachment);
          message.MsgType = ConversationMessageContentType.Audio;
          message.Message = [FileUrl, ...extra].join('|');
          break;
        }
        case 'image': {
          const { FileUrl } = await upload(attachment);
          message.MsgType = ConversationMessageContentType.Image;
          message.Message = FileUrl;
          break;
        }
        case 'video': {
          const { FileUrl } = await upload(attachment);
          message.MsgType = ConversationMessageContentType.Video;
          message.Message = FileUrl;
          break;
        }
        default: {
          const { FileUrl } = await upload(attachment);
          message.MsgType = ConversationMessageContentType.File;
          message.Message = [FileUrl, name, readableFileSize(attachment.size)].join('|');
          break;
        }
      }

      return message;
    } catch (error) {
      console.error(error);
      useMessageStore.getState().delete(message.TimeStamp, type);
      throw error;
    }
  };

  // Ensure ws is connected and send, then handle server warnings uniformly
  const sendOverWebSocket = async (message: SendChatMessageType | SendGroupChatMessageType, isResend: boolean) => {
    if (webSocketManager.state !== 'connected') {
      webSocketManager.reconnect();
      await webSocketManager.ensureConnected(3_000);
    }

    const response = await webSocketManager.dispatchAsync(
      {
        action: 'send',
        params: { message, isResend },
      },
      ['validateMessage'],
    );

    if ('message' in response.params) {
      await queryClient.refetchQueries(generateSessionSettingsQueryOptions(type, id));

      addToast({ type: 'warning', message: response.params.message });

      if (response.params.code === 3) {
        useMessageStore.getState().delete(message.TimeStamp, type);
      }
    }
  };

  const handleSendMessage = async (
    outgoingMessage: SendChatMessageType | SendGroupChatMessageType,
    isResend = false,
  ) => {
    let message = outgoingMessage;

    // First-time send: generate client id and optimistically add to the store
    if (!isResend) {
      message.ClientMsgId = generateClientMessageId(message);
      await useMessageStore.getState().add(transformSendMessageToConversationMessage(message, userInfo));
    }

    if (isMessageAttachmentUploadable(message)) {
      // Convert inline attachment (data/blob URL) to uploaded URL if needed
      message = await uploadAttachmentIfNeeded(message);

      // After converting the inline file to a remote URL, persist the normalized message to the store
      await useMessageStore.getState().add(transformSendMessageToConversationMessage(message, userInfo));
    }

    try {
      await sendOverWebSocket(message, isResend);
    } catch (error) {
      simpleLogger({
        name: SENTRY_LOG.MESSAGES,
        op: 'ws send message failed',
        requestAttributes: { error: JSON.stringify(error) },
      });
      // Fall back to mutation
      await sendMessage(message);
    }
  };

  const submit = useCallback(
    async (data: ConversationFormDataType) => {
      const message = generateMessagePackage(type, id, data);

      if ('Attachment' in data && data.Attachment) {
        for (let i = 0; i < data.Attachment.length; i++) {
          const attachment = data.Attachment[i];
          if (!attachment) continue;

          switch (data.AttachmentType) {
            case FileType.Image:
              message.Message = await convertUploadableToDataURL(attachment);
              message.MsgType = ConversationMessageContentType.Image;
              break;
            case FileType.Video:
              message.Message = await convertUploadableToBlob(attachment).then((blob) => URL.createObjectURL(blob));
              message.MsgType = ConversationMessageContentType.Video;
              break;
            case FileType.Audio:
              message.Message = [await convertUploadableToDataURL(attachment), data.Content].join('|');
              message.MsgType = ConversationMessageContentType.Audio;
              break;
            case FileType.File:
              message.Message = await convertUploadableToBlob(attachment).then((blob) => URL.createObjectURL(blob));
              message.MsgType = ConversationMessageContentType.File;
              break;
            default:
              message.MsgType = ConversationMessageContentType.Text;
              break;
          }

          if (message.Message) {
            const { name, size, type, compress } = attachment;
            const [head, ...tail] = message.Message.split('|');
            message.Message = [head, type, name, size, compress ? '1' : '0', ...tail].join('|');
          }

          // Make each message unique when sending multiple attachments
          message.TimeStamp += i;
          message.ClientMsgId = alphaNumericalNanoId();

          await handleSendMessage(message).catch(noop);
        }
      } else {
        await handleSendMessage(message).catch(noop);
      }
    },
    [id, type],
  );

  return {
    submit,
    retry,
    isLoading: isUploadingFile || isSendingMessage,
  };
}
